"use client";

import { gsap } from "./gsap";

/**
 * Motion vocabulary shared by every section, so the whole page moves with one voice.
 * Values were tuned against the reference: words drift up ~0.4em while de-blurring,
 * CTAs grow out of a dot, cards settle with a long expo tail.
 */

export const DUR = {
  micro: 0.35,
  ui: 0.6,
  reveal: 1.1,
  hero: 1.4,
} as const;

export const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const isMobileViewport = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

type Targets = gsap.TweenTarget;

/** Signature heading reveal: each word rises while de-blurring from a soft smudge. */
export function blurReveal(targets: Targets, vars: gsap.TweenVars = {}) {
  return gsap.fromTo(
    targets,
    { opacity: 0, yPercent: 38, filter: "blur(10px)", willChange: "transform, opacity, filter" },
    {
      opacity: 1,
      yPercent: 0,
      filter: "blur(0px)",
      duration: DUR.reveal,
      ease: "silk",
      stagger: 0.075,
      clearProps: "filter,willChange",
      ...vars,
    },
  );
}

/** Masked reveal: glyphs slide up from behind an overflow mask (logo, small labels). */
export function maskReveal(targets: Targets, vars: gsap.TweenVars = {}) {
  return gsap.fromTo(
    targets,
    { yPercent: 110 },
    { yPercent: 0, duration: 0.9, ease: "premium", stagger: 0.035, ...vars },
  );
}

/*
 * Clip-paths are always animated through registered CSS custom properties (see globals.css).
 * Browsers normalise inset() on read — `inset(0 5% 0 5%)` comes back as `inset(0 5%)` — which
 * breaks string interpolation; animating a typed variable inside a fixed template can't drift.
 */

/** Line reveal: clip-path wipes each line open from the bottom while it lifts into place. */
export function clipReveal(targets: Targets, vars: gsap.TweenVars = {}) {
  gsap.set(targets, { clipPath: "inset(var(--clip-top) -6% -25% -6%)" });
  return gsap.fromTo(
    targets,
    { opacity: 0, y: 30, "--clip-top": "100%" },
    {
      opacity: 1,
      y: 0,
      "--clip-top": "-25%",
      duration: 1,
      ease: "premium",
      stagger: 0.1,
      clearProps: "clipPath",
      ...vars,
    },
  );
}

/** Soft block reveal for paragraphs / eyebrows. */
export function fadeUp(targets: Targets, vars: gsap.TweenVars = {}) {
  return gsap.fromTo(
    targets,
    { opacity: 0, y: 18, filter: "blur(6px)" },
    { opacity: 1, y: 0, filter: "blur(0px)", duration: 0.9, ease: "silk", clearProps: "filter", ...vars },
  );
}

/**
 * The reference's CTA entrance: a dot pops in, then stretches horizontally into the pill
 * (clip-path inset keeps the label anchored while the capsule opens around it).
 */
export function dotToPill(
  el: HTMLElement | null,
  tl: gsap.core.Timeline,
  position: gsap.Position = ">",
) {
  if (!el) return tl;
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  const side = `${Math.max(0, (w - h) / 2)}px`;
  const label = el.querySelector<HTMLElement>("[data-btn-label]");

  gsap.set(el, { "--dot-side": side, clipPath: "inset(0px var(--dot-side) 0px var(--dot-side) round 999px)" });
  tl.fromTo(
    el,
    { opacity: 0, scale: 0.35 },
    { opacity: 1, scale: 1, duration: 0.42, ease: "back.out(1.5)" },
    position,
  ).fromTo(
    el,
    { "--dot-side": side },
    {
      "--dot-side": "0px",
      duration: 0.85,
      ease: "silk",
      // Drop the clip once open so focus rings aren't clipped away.
      onComplete: () => {
        gsap.set(el, { clearProps: "clipPath" });
      },
    },
    ">-0.12",
  );

  if (label) {
    tl.fromTo(
      label,
      { opacity: 0, filter: "blur(4px)" },
      { opacity: 1, filter: "blur(0px)", duration: 0.55, ease: "premium", clearProps: "filter" },
      "<0.08",
    );
  }
  return tl;
}

/** Animates a number inside an element without re-rendering React on every frame. */
export function countTo(
  el: HTMLElement | null,
  to: number,
  { from = 0, decimals = 0, prefix = "", suffix = "", duration = 1.6, ease = "power2.out" } = {},
) {
  if (!el) return gsap.to({}, { duration: 0 });
  const state = { v: from };
  const fmt = (v: number) =>
    `${prefix}${v.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}${suffix}`;
  el.textContent = fmt(from);
  return gsap.to(state, {
    v: to,
    duration,
    ease,
    onUpdate: () => {
      el.textContent = fmt(state.v);
    },
  });
}
